<template>
  <div class="policy-invoice" :class="isMobileView ? 'no-height' : ''">

    <Loading v-if="detailsLoading" />
    <template v-else>
      <div v-if="isMobileView" class="data-record">
        <article class="data-card box box--default">
          <header class="data-card__header">
            <div class="data-card__identity">
              <div class="icon icon__pdf" :class="{ 'icon__pdf--active': invoice.policy_invoice_files.length }"></div>
            </div>
          </header>

          <dl class="data-card__details">
            <div class="data-card__detail">
              <dt>{{ $t(`accountDetails.${"paymentsTable"}.${"header"}.${"status"}`) }}</dt>
              <dd> {{ status }}</dd>
            </div>
            <div class="data-card__detail">
              <dt>Paid {{ $t(`accountDetails.${"paymentsTable"}.${"header"}.${"status"}`) }}</dt>
              <dd style="display: flex; align-items: center; gap: 5px;">
                <span>{{ paidStatus }}</span>
                <div class="policy-invoice__badge" :class="`policy-invoice__badge--${paidStatus}`"></div>
              </dd>
            </div>
            <div class="data-card__detail">
              <dt>Price</dt>
              <dd>{{ `${invoice.price} CHF` }}</dd>
            </div>

            <div v-if="isLast" class="data-card__detail">
              <dt>Total Offen:</dt>
              <dd>
                {{ priceSum }} CHF
              </dd>
            </div>
            <div class="data-card__detail">
              <dt>Date</dt>
              <dd>{{ formatDisplayDate(invoice.date) }}</dd>
            </div>

          </dl>
        </article>
      </div>

      <div v-else class="policy-invoice__row">
        <div class="policy-invoice__data row__data--medium policy-invoice__data--column">
          <div class="policy-invoice__label">{{ status }}</div>
          <div>{{ `${invoice.price} CHF` }}</div>
        </div>
        <div class="policy-invoice__data row__data--medium policy-invoice__data--column">
          <div class="policy-invoice__label">{{ status }}</div>
          <div>{{ formatDisplayDate(invoice.date) }}</div>
        </div>
        <div class="policy-invoice__data row__data--medium policy-invoice__pdf"
          :style="{ cursor: canAccessFiles ? 'pointer' : 'not-allowed' }">
          <div class="icon icon__pdf" :class="{ 'icon__pdf--active': invoice.policy_invoice_files.length }"></div>
          {{ status }}
        </div>
        <div class="policy-invoice__data row__data--medium row__data--underline"
          :class="{ 'row__data--disabled': !canEditInvoice }">

          <div class="policy-invoice__badge" :class="`policy-invoice__badge--${paidStatus}`"></div>
          <span v-if="canEditInvoice" @click="showPaidStatusPopup = true" style="cursor: pointer;">
            <transition name="fade" mode="in-out">
              <PopupEditer v-if="showPaidStatusPopup" @togglePopup="showPaidStatusPopup = $event" :items="paidStatuses"
                :selected="paidStatus" :label="'title'" @submit="updatePaidField($event)" />
            </transition>
            {{ paidStatus }}
          </span>
          <span v-else>
            {{ paidStatus }}
          </span>
        </div>
        <div class="policy-invoice__data row__data--large policy-invoice__price policy-invoice__data--column">
          <div>{{ `${invoice.price} CHF` }}</div>
          <div class="policy-invoice__sum" v-if="isLast">
            Total Offen: {{ priceSum }} CHF
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import PopupEditer from "@/partials/PopupEditer";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import { fetchGlobalCustomer } from "@/helpers/accountDetailHelpers";
//import statics from "@/statics";
import Loading from "@/partials/Loading";

defineOptions({
  name: "Invoice"
});

const { invoice, isLast, priceSum } = defineProps({
  invoice: {
    required: true,
    type: Object,
  },
  isLast: {
    required: true,
    type: Boolean,
  },
  priceSum: {
    required: true,
    type: Number,
  },
});

const store = useStore();
const route = useRoute();

const isMobileView = ref(false);

const updateMobileView = () => {
  isMobileView.value = window.innerWidth <= 900;
};

// State
const showPaidStatusPopup = ref(false);

const detailsLoading = ref(false);

// Computed
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const invoiceTeamId = computed(() => store.getters.customer?.team_id ?? store.getters.customer?.team?.id ?? null);
const canAccessFiles = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("policy_invoice_access", invoiceTeamId.value);
});
const canEditInvoice = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("policy_invoice_edit", invoiceTeamId.value);
});

const status = computed(() => invoice.status.name);

// const statusColor = computed(() => {
//   return (
//     statics.invoiceStatuses.find(s => s.id === invoice.status.id)?.name || "default"
//   );
// });

const paidStatuses = computed(() => [
  { title: "Paid", id: 1 },
  { title: "Unpaid", id: 2 },
]);

const paidStatus = computed(() => (invoice.paid ? "Paid" : "Unpaid"));

// Methods
const updatePaidField = async (paid) => {
  if (!canEditInvoice.value) return;

  detailsLoading.value = true;
  await store.dispatch("updatePaidField", {
    invoiceId: invoice.id,
    paid: paid === 1,
  });
  await fetchGlobalCustomer(route.params.id);
  detailsLoading.value = false;
};

onMounted(() => {
  updateMobileView();
  window.addEventListener("resize", updateMobileView);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateMobileView);
});
</script>

<style lang="scss" scoped>
.policy-invoice {
  border-bottom: 1px solid var(--border-7) !important;

  &:last-child {
    border: 0 !important;
  }

  &__data {
    margin-left: 40px;

    &--column {
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
    }

    .icon__pdf {
      margin-right: 10px;
    }
  }

  &__price {
    align-items: flex-end;
    padding-right: 70px;
  }

  &__label,
  &__price,
  &__sum,
  &__pdf {
    color: var(--text-9);
  }

  &__label {
    font-family: var(--muli-text-bold);
    margin-bottom: 5px;
  }

  &__sum {
    font-family: var(--muli-text-bold);
    margin-top: 7px;
    padding-top: 7px;
    border-top: 1px solid var(--border-12);
  }

  &__badge {
    height: 14px;
    width: 14px;
    border-radius: 50%;
    margin-right: 10px;

    &--operation {
      background: #db2222;
    }

    &--Unpaid {
      background: #db2222;
    }

    &--Paid {
      background: var(--bg-2);
    }

    &--warning {
      background: #ff9e0f;
    }

    &--reminder {
      background: #ffeb3b;
    }

    &--default {
      background: #ccc;
    }
  }
}

.no-height {
  height: auto !important;
  max-height: initial !important;

}

.data-card {
  padding: 14px 0 !important;
}
</style>
